import {
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Alert,
  Chip,
  Paper,
} from '@mui/material';
import type { UrlCheckResult } from '@/features/jobs';

interface UrlSelectionStepProps {
  urlCheckResults: UrlCheckResult[];
  selectedUrls: string[];
  onToggle: (url: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onContinue: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

export default function UrlSelectionStep({
  urlCheckResults,
  selectedUrls,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onContinue,
  onCancel,
  isLoading,
}: UrlSelectionStepProps) {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Selecciona las URLs a traducir
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Las URLs marcadas con <Chip size="small" label="Ya traducida" color="error" sx={{ mx: 0.5 }} /> 
        ya tienen traducciones existentes. Por defecto están desmarcadas para evitar sobrescribirlas.
      </Alert>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button size="small" variant="outlined" onClick={onSelectAll}>
          Seleccionar todos
        </Button>
        <Button size="small" variant="outlined" onClick={onDeselectAll}>
          Deseleccionar todos
        </Button>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          maxHeight: 400,
          overflowY: 'auto',
          p: 2,
          mb: 3,
          backgroundColor: 'grey.50',
        }}
      >
        {urlCheckResults.map((result) => (
          <Box
            key={result.url}
            sx={{
              p: 1.5,
              mb: 1,
              backgroundColor: 'white',
              borderRadius: 1,
              border: '1px solid',
              borderColor: result.isTranslated ? 'error.light' : 'grey.300',
              borderLeftWidth: result.isTranslated ? 4 : 1,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedUrls.includes(result.url)}
                  onChange={() => onToggle(result.url)}
                  disabled={!!result.error}
                />
              }
              label={
                <Box>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {result.url}
                  </Typography>
                  {result.error && (
                    <Typography variant="caption" color="error">
                      ⚠️ Error: {result.error}
                    </Typography>
                  )}
                </Box>
              }
              sx={{ alignItems: 'flex-start', width: '100%', m: 0 }}
            />
            {result.isTranslated && (
              <Chip size="small" label="Ya traducida" color="error" sx={{ ml: 4, mt: 0.5 }} />
            )}
            {!result.isTranslated && !result.error && (
              <Chip size="small" label="Nueva traducción" color="success" sx={{ ml: 4, mt: 0.5 }} />
            )}
          </Box>
        ))}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={isLoading || selectedUrls.length === 0}
        >
          {isLoading ? 'Iniciando...' : `Continuar con ${selectedUrls.length} URL(s)`}
        </Button>
      </Box>
    </Box>
  );
}
